import React, { useMemo, useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { DEFAULT_STATUS_KEY } from '@jira-clone/shared-types'
import {
  DndContext,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { Plus } from 'lucide-react'
import { Button } from '../../../shared/components/ui/Button'
import { Spinner } from '../../../shared/components/ui/Spinner'
import { useUserOptions } from '../../../shared/hooks/useUserOptions'
import {
  useIssues,
  useStatuses,
  useSprints,
  useIssueMutations,
  useSprintMutations,
} from '../hooks'
import {
  IssueFilterBar,
  EMPTY_ISSUE_FILTERS,
  applyIssueFilters,
  type IssueFilters,
} from '../components/IssueFilterBar'
import { ListBulkToolbar } from '../components/ListBulkToolbar'
import { SprintSection } from '../components/SprintSection'
import { CreateSprintModal } from '../components/CreateSprintModal'
import { StartSprintModal } from '../components/StartSprintModal'
import { CompleteSprintModal } from '../components/CompleteSprintModal'
import { NewIssueModal } from '../components/NewIssueModal'
import { IssueModal } from '../components/IssueModal'
import {
  buildEpicLookup,
  buildSubtaskStats,
  orderBetween,
  sortByBacklogOrder,
  doesIssueMatchStatus,
} from '../boardUtils'
import type { Issue, Sprint, WorkflowStatus } from '../api'

export function BacklogPage() {
  const { id: spaceId } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedFromUrl = searchParams.get('selectedIssue')

  // Data fetching
  const { data: rawIssues = [], isLoading: issuesLoading } = useIssues(spaceId ?? null)
  const { data: statuses = [] } = useStatuses(spaceId ?? null)
  const { data: rawSprints = [], isLoading: sprintsLoading } = useSprints(spaceId ?? null)
  const userQuery = useUserOptions(spaceId ?? null)

  // Backlog is one of the fixed statuses (key 'backlog'), the same in every space.
  const backlogStatus = useMemo(() => statuses.find((s) => s.id === DEFAULT_STATUS_KEY), [statuses])

  // Mutations
  const { create, update, remove } = useIssueMutations(spaceId ?? '')
  const { createSprint, updateSprint, deleteSprint } = useSprintMutations(spaceId ?? '')

  // Filters & selection state
  const [filters, setFilters] = useState<IssueFilters>(EMPTY_ISSUE_FILTERS)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)

  // Modals state
  const [openIssueId, setOpenIssueId] = useState<string | null>(selectedFromUrl)
  const [newIssueOpen, setNewIssueOpen] = useState(false)
  const [createSprintOpen, setCreateSprintOpen] = useState(false)
  const [startSprintTarget, setStartSprintTarget] = useState<Sprint | null>(null)
  const [completeSprintTarget, setCompleteSprintTarget] = useState<Sprint | null>(null)

  useEffect(() => {
    if (selectedFromUrl !== openIssueId) {
      setOpenIssueId(selectedFromUrl)
    }
  }, [selectedFromUrl])

  const handleOpenIssue = (id: string) => {
    setOpenIssueId(id)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('selectedIssue', id)
      return next
    })
  }

  const handleCloseIssue = () => {
    setOpenIssueId(null)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('selectedIssue')
      return next
    })
  }

  // DnD sensors (distance constraint prevents clicks from triggering drags)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  )

  const allUsers = useMemo(() => userQuery.data ?? [], [userQuery.data])

  const assigneeNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const u of allUsers) {
      map.set(u.id, u.display_name)
    }
    return map
  }, [allUsers])

  // Sync Sprints (purely from database / optimistic updates)
  const sprints = useMemo(() => {
    if (rawSprints.length > 0) {
      return (rawSprints as Sprint[]).map((s) => ({
        ...s,
        status: s.status || 'planning',
      }))
    }
    return []
  }, [rawSprints])

  // Sync Issues (purely from database / optimistic updates)
  const issues = useMemo(() => {
    if (rawIssues.length > 0) {
      return rawIssues as Issue[]
    }
    return []
  }, [rawIssues])

  // Filter issues
  const filteredIssues = useMemo(() => {
    return applyIssueFilters(issues, filters, (id) => id ?? '')
  }, [issues, filters])

  // Lookups
  const epicLookup = useMemo(() => buildEpicLookup(issues), [issues])
  const subtaskStats = useMemo(() => buildSubtaskStats(issues), [issues])

  // Group issues by sprint
  const sprintSections = useMemo(() => {
    // 1. Active sprint
    const active = sprints.find((s) => s.status === 'active')
    // 2. Future / Planning sprints
    const futures = sprints.filter((s) => s.status !== 'active' && s.status !== 'completed')

    const sprintMap = new Map<string, Issue[]>()
    const backlogList: Issue[] = []

    for (const issue of filteredIssues) {
      // Epics and subtasks don't show up as primary backlog cards
      if (issue.type === 'epic' || issue.parent_id) continue

      const isBacklogStatus = doesIssueMatchStatus(issue, backlogStatus, statuses)

      if (isBacklogStatus) {
        // ONLY tasks with status as "Backlog" appear in the Backlog section
        backlogList.push(issue)
      } else if (issue.sprint_id && issue.sprint_id !== 'backlog') {
        // Active or future sprint issues
        const list = sprintMap.get(issue.sprint_id) ?? []
        list.push(issue)
        sprintMap.set(issue.sprint_id, list)
      }
      // If neither (not in a sprint and status is not Backlog, e.g. To Do, In Progress, Done):
      // they belong on the Board and List, NOT in the Backlog section!
    }

    return {
      activeSprint: active
        ? {
            sprint: active,
            issues: sortByBacklogOrder(sprintMap.get(active.id) ?? []),
          }
        : null,
      futureSprints: futures.map((s) => ({
        sprint: s,
        issues: sortByBacklogOrder(sprintMap.get(s.id) ?? []),
      })),
      backlogIssues: sortByBacklogOrder(backlogList),
    }
  }, [sprints, filteredIssues, backlogStatus, statuses])

  // Flatten ordered list for shift+click range selection
  const flatVisibleIssues = useMemo(() => {
    const list: Issue[] = []
    if (sprintSections.activeSprint) {
      list.push(...sprintSections.activeSprint.issues)
    }
    for (const fs of sprintSections.futureSprints) {
      list.push(...fs.issues)
    }
    list.push(...sprintSections.backlogIssues)
    return list
  }, [sprintSections])

  // Selection toggle
  const handleToggleSelect = (issueId: string, shiftKey: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (shiftKey && lastSelectedId) {
        const lastIdx = flatVisibleIssues.findIndex((i) => i.id === lastSelectedId)
        const curIdx = flatVisibleIssues.findIndex((i) => i.id === issueId)
        if (lastIdx !== -1 && curIdx !== -1) {
          const [start, end] = lastIdx < curIdx ? [lastIdx, curIdx] : [curIdx, lastIdx]
          for (let i = start; i <= end; i++) {
            next.add(flatVisibleIssues[i].id)
          }
          return next
        }
      }

      if (next.has(issueId)) {
        next.delete(issueId)
      } else {
        next.add(issueId)
      }
      return next
    })
    setLastSelectedId(issueId)
  }

  // Quick update status handler
  const handleUpdateStatus = async (issueId: string, statusId: string, category: string) => {
    try {
      await update.mutateAsync({
        id: issueId,
        payload: {
          status: statusId,
          status_category: category as any,
        },
      })
      toast.success('Status updated')
    } catch {
      toast.error('Could not update status')
    }
  }

  // Drag and drop handler
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeId = String(active.id)
    const overId = String(over.id)

    const draggedIssue = issues.find((i) => i.id === activeId)
    if (!draggedIssue) return

    let targetSectionId: string | null = null
    let targetSprintId: string | null = null
    let newOrder = 1000

    if (overId.startsWith('section:')) {
      // Dropped onto section container
      targetSectionId = overId.replace('section:', '')
      targetSprintId = targetSectionId === 'backlog' ? null : targetSectionId

      // Find issues in that section
      const targetList = issues
        .filter((i) =>
          targetSectionId === 'backlog'
            ? !i.sprint_id || i.sprint_id === 'backlog'
            : i.sprint_id === targetSectionId,
        )
        .filter((i) => i.id !== activeId)
      const sorted = sortByBacklogOrder(targetList)
      const last = sorted[sorted.length - 1]
      newOrder = (last?.backlog_order ?? 0) + 1000
    } else {
      // Dropped onto another issue
      const overIssue = issues.find((i) => i.id === overId)
      if (!overIssue) return

      targetSprintId = overIssue.sprint_id ?? null
      targetSectionId = targetSprintId || 'backlog'

      const targetList = issues
        .filter((i) =>
          targetSectionId === 'backlog'
            ? !i.sprint_id || i.sprint_id === 'backlog'
            : i.sprint_id === targetSectionId,
        )
        .filter((i) => i.id !== activeId)
      const sorted = sortByBacklogOrder(targetList)
      const overIdx = sorted.findIndex((i) => i.id === overId)

      const prev = overIdx > 0 ? sorted[overIdx - 1] : undefined
      const next = sorted[overIdx]

      newOrder = orderBetween(prev?.backlog_order, next?.backlog_order)
    }

    const isTargetBacklog = targetSectionId === 'backlog'
    let updatedStatusId: string | undefined = undefined

    if (isTargetBacklog) {
      if (backlogStatus) updatedStatusId = backlogStatus.id
    } else if (draggedIssue && doesIssueMatchStatus(draggedIssue, backlogStatus, statuses)) {
      const todoStatus = statuses.find((s) => s.category === 'todo' && s.id !== backlogStatus?.id)
      if (todoStatus) updatedStatusId = todoStatus.id
    }

    // Optimistically update React Query cache
    queryClient.setQueryData<Issue[]>(['spaces', 'issues', spaceId], (prev = []) =>
      prev.map((i) =>
        i.id === activeId
          ? {
              ...i,
              sprint_id: targetSprintId,
              backlog_order: newOrder,
              ...(updatedStatusId ? { status: updatedStatusId } : {}),
            }
          : i,
      ),
    )

    try {
      const payload: any = {
        sprint_id: targetSprintId,
        backlog_order: newOrder,
      }
      if (updatedStatusId) {
        payload.status = updatedStatusId
      }
      await update.mutateAsync({
        id: activeId,
        payload,
      })
      toast.success('Moved issue')
    } catch {
      toast.error('Could not move issue')
      queryClient.invalidateQueries({ queryKey: ['spaces', 'issues', spaceId] })
    }
  }

  // Quick create issue
  const handleQuickCreateIssue = async (title: string, sprintId?: string | null) => {
    try {
      const nextOrder = (issues[issues.length - 1]?.backlog_order ?? 0) + 1000
      const isBacklog = !sprintId || sprintId === 'backlog'
      const statusId = isBacklog
        ? backlogStatus?.id || 'backlog'
        : statuses.find((s) => s.category === 'todo')?.id || 'todo'

      await create.mutateAsync({
        title,
        type: 'task',
        priority: 'medium',
        status: statusId,
        sprint_id: sprintId === 'backlog' ? null : sprintId ?? null,
        backlog_order: nextOrder,
        story_points: 1,
      } as any)

      toast.success('Created issue')
    } catch {
      toast.error('Could not create issue')
    }
  }

  // Create sprint
  const handleCreateSprint = async (payload: {
    name: string
    start_date?: string
    end_date?: string
    goal?: string
  }) => {
    try {
      await createSprint.mutateAsync(payload)
      toast.success(`Created sprint "${payload.name}"`)
    } catch {
      toast.error('Could not create sprint')
    }
  }

  // Start sprint confirmation
  const handleConfirmStartSprint = async (payload: {
    name: string
    start_date: string
    end_date: string
    goal?: string
  }) => {
    if (!startSprintTarget) return
    try {
      await updateSprint.mutateAsync({
        id: startSprintTarget.id,
        payload: {
          ...payload,
          status: 'active',
        },
      })
      toast.success(`Started ${payload.name}!`)
    } catch {
      toast.error('Could not start sprint')
    }
  }

  // Complete sprint confirmation
  const handleConfirmCompleteSprint = async (moveToSprintId: string | 'backlog') => {
    if (!completeSprintTarget) return
    try {
      await updateSprint.mutateAsync({
        id: completeSprintTarget.id,
        payload: { status: 'completed' },
      })

      // Move incomplete issues
      const targetSprint = moveToSprintId === 'backlog' ? null : moveToSprintId
      const incomplete = issues.filter(
        (i) =>
          i.sprint_id === completeSprintTarget.id &&
          i.status_category !== 'done',
      )

      for (const inc of incomplete) {
        await update.mutateAsync({
          id: inc.id,
          payload: {
            sprint_id: targetSprint,
            ...(targetSprint === null && backlogStatus ? { status: backlogStatus.id } : {}),
          },
        })
      }

      toast.success(`Completed ${completeSprintTarget.name}!`)
    } catch {
      toast.error('Could not complete sprint')
    }
  }

  // Delete sprint
  const handleDeleteSprint = async (sprintId: string) => {
    try {
      await deleteSprint.mutateAsync(sprintId)
      toast.success('Sprint deleted')
    } catch {
      toast.error('Could not delete sprint')
    }
  }

  // Bulk actions runner
  const runBulk = async (fn: (id: string) => Promise<unknown>, msg: string) => {
    setBulkBusy(true)
    try {
      await Promise.all(Array.from(selected).map(fn))
      toast.success(msg)
      setSelected(new Set())
    } catch {
      toast.error('Bulk operation failed')
    } finally {
      setBulkBusy(false)
    }
  }

  const activeModalIssue = useMemo(() => {
    const targetId = openIssueId || selectedFromUrl
    if (!targetId) return null
    return issues.find((i) => i.id === targetId || i.key === targetId) ?? null
  }, [openIssueId, selectedFromUrl, issues])

  const nextSprintNumber = sprints.length + 1

  return (
    <div className="flex h-full flex-col bg-white dark:bg-slate-950 font-sans">
      <div className="flex min-h-0 flex-1 flex-col p-6 overflow-y-auto">
        {/* Top Controls: Filter Bar & Sprint Actions */}
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <IssueFilterBar
                filters={filters}
                onChange={setFilters}
                members={allUsers}
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCreateSprintOpen(true)}
              >
                Create Sprint
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setNewIssueOpen(true)}
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" />
                <span>Create issue</span>
              </Button>
            </div>
          </div>
        </div>

        {issuesLoading || sprintsLoading ? (
          <div className="flex justify-center py-20">
            <Spinner />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={handleDragEnd}
          >
            <div className="space-y-4 pb-20">
              {/* 1. Active Sprint */}
              {sprintSections.activeSprint && (
                <SprintSection
                  key={sprintSections.activeSprint.sprint.id}
                  sprint={sprintSections.activeSprint.sprint}
                  sectionId={sprintSections.activeSprint.sprint.id}
                  issues={sprintSections.activeSprint.issues}
                  epicLookup={epicLookup}
                  subtaskStats={subtaskStats}
                  assigneeNameById={assigneeNameById}
                  statuses={statuses}
                  onUpdateStatus={handleUpdateStatus}
                  onOpenIssue={handleOpenIssue}
                  onCreateIssue={handleQuickCreateIssue}
                  onCompleteSprint={() =>
                    setCompleteSprintTarget(sprintSections.activeSprint!.sprint)
                  }
                  onDeleteSprint={() =>
                    handleDeleteSprint(sprintSections.activeSprint!.sprint.id)
                  }
                  selected={selected}
                  onToggleSelect={handleToggleSelect}
                />
              )}

              {/* 2. Future / Planning Sprints */}
              {sprintSections.futureSprints.map(({ sprint, issues: sIssues }) => (
                <SprintSection
                  key={sprint.id}
                  sprint={sprint}
                  sectionId={sprint.id}
                  issues={sIssues}
                  epicLookup={epicLookup}
                  subtaskStats={subtaskStats}
                  assigneeNameById={assigneeNameById}
                  statuses={statuses}
                  onUpdateStatus={handleUpdateStatus}
                  onOpenIssue={handleOpenIssue}
                  onCreateIssue={handleQuickCreateIssue}
                  onStartSprint={() => setStartSprintTarget(sprint)}
                  onDeleteSprint={() => handleDeleteSprint(sprint.id)}
                  selected={selected}
                  onToggleSelect={handleToggleSelect}
                />
              ))}

              {/* 3. Backlog Section */}
              <SprintSection
                sprint={null}
                sectionId="backlog"
                issues={sprintSections.backlogIssues}
                epicLookup={epicLookup}
                subtaskStats={subtaskStats}
                assigneeNameById={assigneeNameById}
                statuses={statuses}
                onUpdateStatus={handleUpdateStatus}
                onOpenIssue={handleOpenIssue}
                onCreateIssue={handleQuickCreateIssue}
                selected={selected}
                onToggleSelect={handleToggleSelect}
              />
            </div>
          </DndContext>
        )}
      </div>

      {/* Bulk action floating toolbar */}
      <ListBulkToolbar
        selectedCount={selected.size}
        totalCount={flatVisibleIssues.length}
        statuses={statuses}
        onSelectAll={() => setSelected(new Set(flatVisibleIssues.map((i) => i.id)))}
        onClear={() => setSelected(new Set())}
        busy={bulkBusy}
        onBulkStatus={(statusId: string) =>
          runBulk(
            (id) => update.mutateAsync({ id, payload: { status: statusId } }),
            `Moved ${selected.size} issues`,
          )
        }
        onBulkPriority={(priority: string) =>
          runBulk(
            (id) => update.mutateAsync({ id, payload: { priority: priority as any } }),
            `Updated priority for ${selected.size} issues`,
          )
        }
        onBulkDelete={() =>
          runBulk((id) => remove.mutateAsync(id), `Deleted ${selected.size} issues`)
        }
      />

      {/* Create Sprint Modal */}
      <CreateSprintModal
        open={createSprintOpen}
        onClose={() => setCreateSprintOpen(false)}
        defaultName={`Sprint ${nextSprintNumber}`}
        onSubmit={handleCreateSprint}
      />

      {/* Start Sprint Modal */}
      <StartSprintModal
        sprint={startSprintTarget}
        issueCount={
          startSprintTarget
            ? issues.filter((i) => i.sprint_id === startSprintTarget.id).length
            : 0
        }
        open={Boolean(startSprintTarget)}
        onClose={() => setStartSprintTarget(null)}
        onConfirm={handleConfirmStartSprint}
      />

      {/* Complete Sprint Modal */}
      <CompleteSprintModal
        sprint={completeSprintTarget}
        completedCount={
          completeSprintTarget
            ? issues.filter(
                (i) =>
                  i.sprint_id === completeSprintTarget.id &&
                  i.status_category === 'done',
              ).length
            : 0
        }
        openCount={
          completeSprintTarget
            ? issues.filter(
                (i) =>
                  i.sprint_id === completeSprintTarget.id &&
                  i.status_category !== 'done',
              ).length
            : 0
        }
        open={Boolean(completeSprintTarget)}
        onClose={() => setCompleteSprintTarget(null)}
        onConfirm={handleConfirmCompleteSprint}
        otherSprints={sprints}
      />

      {/* New Issue Modal */}
      <NewIssueModal
        open={newIssueOpen}
        onClose={() => setNewIssueOpen(false)}
        spaceId={spaceId ?? ''}
      />

      {/* Issue Details Modal */}
      {activeModalIssue && (
        <IssueModal
          issue={activeModalIssue}
          open={Boolean(activeModalIssue)}
          onClose={handleCloseIssue}
          statuses={statuses}
          members={allUsers}
          epics={issues.filter((i) => i.type === 'epic')}
          onUpdate={async (id, payload) => {
            try {
              await update.mutateAsync({ id, payload })
              toast.success('Updated issue')
            } catch {
              toast.error('Could not update issue')
            }
          }}
          onDelete={async (id) => {
            try {
              await remove.mutateAsync(id)
              handleCloseIssue()
              toast.success('Deleted issue')
            } catch {
              toast.error('Could not delete issue')
            }
          }}
        />
      )}
    </div>
  )
}
